import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import { InstantSchema } from './time';

/**
 * Archive, restore and controlled permanent deletion of master records (ADR-0047).
 *
 * - **Archiving** hides a record from active lists, pickers, search and new work. Nothing about it is
 *   changed or removed; its history, relationships and money stay exactly as they were, and a person
 *   with the permission restores it. It is not a business status: a lost lead stays lost, a paused
 *   project stays paused, whether archived or not.
 * - **Permanent deletion** exists only for a record entered by mistake that nothing else references —
 *   no child record, no money, no stock, no document, no workflow. The server proves it inside the
 *   deleting transaction; a tombstone and the audit trail keep that the record existed and who removed
 *   it, without copying its personal data.
 * - Financial, contractual, inventory-history and audit records are never deletable (ADR-0009): they
 *   are cancelled, reversed or closed through their own workflow.
 */
export const LIFECYCLE_RECORD_TYPES = [
  'lead',
  'customer',
  'opportunity',
  'project',
  'building',
  'unit',
] as const;
export const LifecycleRecordTypeSchema = z.enum(LIFECYCLE_RECORD_TYPES);
export type LifecycleRecordType = z.infer<typeof LifecycleRecordTypeSchema>;

const ReasonSchema = z.string().trim().min(3).max(500);

/** Who archived the record, when and why. Present only while the record is archived. */
export const ArchiveMarkSchema = z.strictObject({
  at: InstantSchema,
  by: z.string().min(1).max(200),
  reason: z.string().max(500),
});
export type ArchiveMark = z.infer<typeof ArchiveMarkSchema>;

export const ArchiveRecordSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  reason: ReasonSchema,
});
export type ArchiveRecord = z.infer<typeof ArchiveRecordSchema>;

export const RestoreRecordSchema = ArchiveRecordSchema;
export type RestoreRecord = ArchiveRecord;

/**
 * Permanent deletion: the version read, a reason, and the record's name or code typed back exactly as
 * the confirmation shows it — so a deletion is never one careless click.
 */
export const DeleteRecordSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  reason: ReasonSchema,
  confirmation: z.string().trim().min(1).max(200),
});
export type DeleteRecord = z.infer<typeof DeleteRecordSchema>;

/** Which records a list shows: the active ones (the default), the archived ones, or both. */
export const ARCHIVE_VIEWS = ['active', 'archived', 'all'] as const;
export const ArchiveViewSchema = z.enum(ARCHIVE_VIEWS);
export type ArchiveView = z.infer<typeof ArchiveViewSchema>;

/**
 * What stops an action, by kind — never a description of the blocking record, which the actor may not
 * be allowed to see. `count` is a lower bound when the census stops early.
 */
export const LifecycleBlockerSchema = z.strictObject({
  code: z.string().regex(/^[a-zA-Z][a-zA-Z0-9]{0,63}$/),
  count: z.number().int().nonnegative().optional(),
});
export type LifecycleBlocker = z.infer<typeof LifecycleBlockerSchema>;

/** The deletion check: whether permanent deletion is possible now, and if not, why. Writes nothing. */
export const DeletionCheckSchema = z.strictObject({
  recordType: LifecycleRecordTypeSchema,
  recordId: RecordIdSchema,
  eligible: z.boolean(),
  /** What the confirmation must repeat: the record's name or code. */
  confirmationText: z.string(),
  blockers: z.array(LifecycleBlockerSchema),
});
export type DeletionCheck = z.infer<typeof DeletionCheckSchema>;

/** A deleted record's remainder: its type, identifier, who removed it, when and why. */
export const DeletionResultSchema = z.strictObject({
  recordType: LifecycleRecordTypeSchema,
  recordId: RecordIdSchema,
  deletedAt: InstantSchema,
  /** True when the request repeats one already carried out: nothing further happened. */
  replayed: z.boolean(),
});
export type DeletionResult = z.infer<typeof DeletionResultSchema>;

export const LIFECYCLE_AUDIT_ACTIONS = {
  archived: 'record.archived',
  restored: 'record.restored',
  archiveRefused: 'record.archiveRefused',
  deletedPermanently: 'record.deletedPermanently',
  deletionRefused: 'record.deletionRefused',
} as const;
